import React from 'react';

/**
 * The animated red/black backdrop behind the sign-in screen.
 *
 * Extracted so the forced password-change screen — which a user reaches
 * straight from signing in — can sit on the identical background instead of a
 * flat panel that looks like a different product. One copy, so the two cannot
 * drift apart.
 *
 * Purely decorative: absolutely positioned at zIndex 0 with pointerEvents
 * none, so it never intercepts a click meant for the form above it.
 */
export default function AuthBackdrop({ isDark = true }) {
  return (
    <>
    {/* ═══ ANIMATED BACKGROUND LAYERS ═══ */}
    <style>{`
      @keyframes blob1 {
        0%, 100% { transform: translate(0, 0) scale(1); }
        25% { transform: translate(60px, -80px) scale(1.15); }
        50% { transform: translate(-40px, 50px) scale(0.85); }
        75% { transform: translate(30px, 20px) scale(1.05); }
      }
      @keyframes blob2 {
        0%, 100% { transform: translate(0, 0) scale(1); }
        25% { transform: translate(-70px, 50px) scale(0.9); }
        50% { transform: translate(50px, -40px) scale(1.1); }
        75% { transform: translate(-20px, -60px) scale(0.95); }
      }
      @keyframes blob3 {
        0%, 100% { transform: translate(0, 0) scale(1); }
        33% { transform: translate(30px, 70px) scale(1.12); }
        66% { transform: translate(-60px, -30px) scale(0.88); }
      }
      @keyframes floatShape {
        0%, 100% { transform: translateY(0) rotate(0deg); }
        50% { transform: translateY(-20px) rotate(180deg); }
      }
      @keyframes floatShapeSlow {
        0%, 100% { transform: translateY(0) rotate(0deg); }
        50% { transform: translateY(-30px) rotate(-120deg); }
      }
      @keyframes pulse {
        0%, 100% { opacity: 0.4; }
        50% { opacity: 0.8; }
      }
      @keyframes gridSlide {
        0% { transform: translateY(0); }
        100% { transform: translateY(60px); }
      }
    `}</style>

    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 0, pointerEvents: 'none' }}>
      {/* ── Layer 1: Animated gradient orbs ── */}
      <div style={{
        position: 'absolute', top: '-5%', left: '-5%', width: '50vw', height: '50vw', maxWidth: 700, maxHeight: 700,
        background: 'radial-gradient(circle, #991b1b 0%, transparent 70%)', borderRadius: '50%',
        filter: 'blur(100px)', opacity: isDark ? 0.4 : 0.2, zIndex: 0,
        animation: 'blob1 20s ease-in-out infinite'
      }} />
      <div style={{
        position: 'absolute', bottom: '-10%', right: '-5%', width: '45vw', height: '45vw', maxWidth: 600, maxHeight: 600,
        background: 'radial-gradient(circle, #dc2626 0%, transparent 70%)', borderRadius: '50%',
        filter: 'blur(90px)', opacity: isDark ? 0.3 : 0.15, zIndex: 0,
        animation: 'blob2 25s ease-in-out infinite'
      }} />
      <div style={{
        position: 'absolute', top: '40%', left: '50%', width: '30vw', height: '30vw', maxWidth: 400, maxHeight: 400,
        background: 'radial-gradient(circle, #7f1d1d 0%, transparent 70%)', borderRadius: '50%',
        filter: 'blur(80px)', opacity: isDark ? 0.25 : 0.1, zIndex: 0,
        animation: 'blob3 18s ease-in-out infinite'
      }} />
      {/* Accent blue-tinted orb for depth contrast */}
      <div style={{
        position: 'absolute', top: '60%', left: '15%', width: '20vw', height: '20vw', maxWidth: 300, maxHeight: 300,
        background: 'radial-gradient(circle, #1e1b4b 0%, transparent 70%)', borderRadius: '50%',
        filter: 'blur(80px)', opacity: isDark ? 0.2 : 0.08, zIndex: 0,
        animation: 'blob2 22s ease-in-out infinite reverse'
      }} />

      {/* ── Layer 2: Animated grid lines ── */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0, opacity: isDark ? 0.06 : 0.04,
        backgroundImage: `
          linear-gradient(${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)'} 1px, transparent 1px),
          linear-gradient(90deg, ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)'} 1px, transparent 1px)
        `,
        backgroundSize: '60px 60px',
        animation: 'gridSlide 8s linear infinite',
      }} />

      {/* ── Layer 3: Floating geometric shapes ── */}
      {/* Diamond */}
      <div style={{
        position: 'absolute', top: '15%', right: '18%', width: 20, height: 20,
        border: `2px solid ${isDark ? 'rgba(239,68,68,0.25)' : 'rgba(153,27,27,0.15)'}`,
        transform: 'rotate(45deg)', zIndex: 0,
        animation: 'floatShape 6s ease-in-out infinite',
      }} />
      {/* Small circle */}
      <div style={{
        position: 'absolute', top: '25%', left: '12%', width: 12, height: 12,
        borderRadius: '50%', background: isDark ? 'rgba(239,68,68,0.2)' : 'rgba(153,27,27,0.1)',
        zIndex: 0, animation: 'floatShapeSlow 8s ease-in-out infinite',
      }} />
      {/* Ring */}
      <div style={{
        position: 'absolute', bottom: '20%', left: '20%', width: 28, height: 28,
        borderRadius: '50%', border: `2px solid ${isDark ? 'rgba(239,68,68,0.2)' : 'rgba(153,27,27,0.12)'}`,
        zIndex: 0, animation: 'floatShape 10s ease-in-out infinite 1s',
      }} />
      {/* Cross / Plus */}
      <div style={{
        position: 'absolute', top: '70%', right: '12%', width: 16, height: 16, zIndex: 0,
        animation: 'floatShapeSlow 7s ease-in-out infinite 2s',
      }}>
        <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 2, background: isDark ? 'rgba(239,68,68,0.25)' : 'rgba(153,27,27,0.15)', transform: 'translateY(-50%)' }} />
        <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 2, background: isDark ? 'rgba(239,68,68,0.25)' : 'rgba(153,27,27,0.15)', transform: 'translateX(-50%)' }} />
      </div>
      {/* Small diamond bottom-left */}
      <div style={{
        position: 'absolute', bottom: '35%', left: '8%', width: 10, height: 10,
        border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
        transform: 'rotate(45deg)', zIndex: 0,
        animation: 'floatShape 9s ease-in-out infinite 3s',
      }} />
      {/* Dot cluster top-left */}
      <div style={{ position: 'absolute', top: '12%', left: '30%', zIndex: 0, animation: 'pulse 4s ease-in-out infinite' }}>
        {[0, 1, 2].map(r => (
          <div key={r} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            {[0, 1, 2].map(c => (
              <div key={c} style={{ width: 3, height: 3, borderRadius: '50%', background: isDark ? 'rgba(239,68,68,0.3)' : 'rgba(153,27,27,0.15)' }} />
            ))}
          </div>
        ))}
      </div>
      {/* Dot cluster bottom-right */}
      <div style={{ position: 'absolute', bottom: '12%', right: '25%', zIndex: 0, animation: 'pulse 5s ease-in-out infinite 1.5s' }}>
        {[0, 1].map(r => (
          <div key={r} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            {[0, 1, 2, 3].map(c => (
              <div key={c} style={{ width: 2.5, height: 2.5, borderRadius: '50%', background: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)' }} />
            ))}
          </div>
        ))}
      </div>

      {/* ── Layer 4: Radial vignette overlay ── */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0,
        background: `radial-gradient(ellipse at center, transparent 40%, ${isDark ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.08)'} 100%)`,
      }} />

      {/* ── Layer 5: Subtle noise texture ── */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0, opacity: isDark ? 0.03 : 0.02,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
      }} />
    </div>
    </>
  );
}
